import React, { useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  HStack,
  Select,
  TextareaField,
  TextField,
  VStack,
} from '@rtkwlf/fenrir-react';
import type { CredentialInput } from '@rtkwlf/rendall';
import type { FieldError, Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldWrapper } from '@packages/components/field-wrapper/field-wrapper';
import { EventKey } from 'ae-constants/constants';

import { TargetMultiSelect } from '../../../../../../../../../../scan-schedules/components/new-scan-schedules/target-multi-select';
import { CredentialedScanningType } from '../../types';
import { useCreateCredentialedScanWrapper } from '../../use-create-credentialed-scan-wrapper';
import type { UpdateCredential } from '../../use-update-iva-credential-scan-wrapper';
import { useUpdateIVACredentialScanWrapper } from '../../use-update-iva-credential-scan-wrapper';

import { useCredTypeOptions } from './use-cred-type-options/use-cred-type-options';
import { useScanCredentialFormValidationSchema } from './use-scan-credential-form-validation-schema/use-scan-credential-form-validation-schema';

const defaultBlankForm: CredentialInput = {
  description: '',
  name: '',
  password: '',
  targets: [] as string[],
  type: CredentialedScanningType.Up,
  username: '',
};

type ScanCredentialFormProps = {
  onClose: () => void;
  scannerId: string;
  credentialId?: string;
  scanCredentialFormData?: CredentialInput;
};

export const ScanCredentialForm: React.FC<ScanCredentialFormProps> = ({
  scannerId,
  scanCredentialFormData,
  onClose,
  credentialId,
}) => {
  const [credType, setCredType] = useState<CredentialedScanningType>(
    CredentialedScanningType.Up,
  );
  const [formHasError, setFormHasError] = useState<boolean>(false);
  const [scanTargetsErrors, setScanTargetsErrors] = useState<FieldError[]>([]);
  const { t } = useTranslation();
  const { t: tScan } = useTranslation('telemetryScannerConfig');
  const {
    mutate: addCredentialedScan,
    isPending: isAddCredentialedScanMutationPending,
  } = useCreateCredentialedScanWrapper(scannerId);
  const {
    mutate: updateCredentialedScan,
    isPending: isUpdateCredentialedScanMutationPending,
  } = useUpdateIVACredentialScanWrapper(scannerId);
  const credTypeOptions = useCredTypeOptions();
  const initialFormData = useMemo(() => {
    if (scanCredentialFormData) {
      setCredType(
        scanCredentialFormData.type === 'up'
          ? CredentialedScanningType.Up
          : CredentialedScanningType.Usk,
      );
      return { ...scanCredentialFormData };
    }
    return { ...defaultBlankForm };
  }, [scanCredentialFormData]);

  const validationSchema = useScanCredentialFormValidationSchema(credType);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: initialFormData,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema) as Resolver<CredentialInput>,
    shouldUnregister: true,
  });

  const targetListData = useMemo(() => {
    const targets = scanCredentialFormData?.targets.length
      ? scanCredentialFormData.targets.map((target) => ({
          label: target,
          value: target,
        }))
      : [];
    return targets;
  }, [scanCredentialFormData]);

  const preventSubmitOnEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === EventKey.Return) e.preventDefault();
  };

  const onSubmit = (formData: CredentialInput) => {
    if (scanCredentialFormData) {
      const updateCredential: UpdateCredential = {
        credentialId: parseInt(credentialId as string, 10),
        credentialInput: formData,
      };
      updateCredentialedScan(updateCredential, {
        onSuccess: onClose,
      });
    } else {
      addCredentialedScan(formData, {
        onSuccess: onClose,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventSubmitOnEnter}>
      <VStack data-testid="create-credential-form">
        <FieldWrapper
          data-testid="create-credential-form-name"
          maxWidth="40rem"
          label={tScan('credentialedScanningForm.name')}
          error={errors.name}
          isRequired
          field={
            <Controller
              control={control}
              name="name"
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField {...rest} value={value} onChange={onChange} />
              )}
            />
          }
        />
        <FieldWrapper
          data-testid="create-credential-form-description"
          maxWidth="40rem"
          label={tScan('credentialedScanningForm.description')}
          error={errors.description}
          field={
            <Controller
              control={control}
              name="description"
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField {...rest} value={value} onChange={onChange} />
              )}
            />
          }
        />
        <FieldWrapper
          data-testid="create-credential-form-targets"
          maxWidth="40rem"
          label={tScan('credentialedScanningForm.addTargets')}
          fieldInfo={tScan('credentialedScanningForm.targetNote')}
          error={errors.targets || scanTargetsErrors}
          field={
            <Controller
              control={control}
              name="targets"
              render={({ field: { ref: _ref, onChange, ...rest } }) => (
                <TargetMultiSelect
                  {...rest}
                  setValue={(selections) => {
                    setValue('targets', selections.sort(), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  items={targetListData}
                  setTargetErrors={(targetErrors) => {
                    setScanTargetsErrors(targetErrors);
                    if (targetErrors.length > 0) setFormHasError(true);
                    else setFormHasError(false);
                  }}
                />
              )}
            />
          }
        />
        <FieldWrapper
          data-testid="create-credential-form-type"
          maxWidth="14rem"
          label={tScan('credentialedScanningForm.type')}
          field={
            <Controller
              control={control}
              name="type"
              render={({ field: { ref: _ref, onChange, ...rest } }) => (
                <Select
                  {...rest}
                  options={credTypeOptions}
                  value={credType}
                  isClearable={false}
                  onSelection={(selection) => {
                    onChange(selection?.value);
                    setValue('password', '');
                    setCredType(selection?.value as CredentialedScanningType);
                  }}
                />
              )}
            />
          }
        />
        <FieldWrapper
          data-testid="create-credential-form-username"
          maxWidth="40rem"
          label={tScan('credentialedScanningForm.username')}
          error={errors.username}
          isRequired
          field={
            <Controller
              control={control}
              name="username"
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField {...rest} value={value} onChange={onChange} />
              )}
            />
          }
        />

        <FieldWrapper
          data-testid={
            credType === CredentialedScanningType.Up
              ? 'create-credential-form-password'
              : 'create-credential-form-passphrase'
          }
          maxWidth="40rem"
          label={
            credType === CredentialedScanningType.Up
              ? tScan('credentialedScanningForm.password')
              : tScan('credentialedScanningForm.passphrase')
          }
          error={errors.password}
          field={
            <Controller
              control={control}
              name="password"
              render={({ field: { value, onChange, ...rest } }) => (
                <TextField
                  {...rest}
                  value={value ?? ''}
                  onChange={onChange}
                  type="password"
                />
              )}
            />
          }
        />

        {credType === CredentialedScanningType.Usk && (
          <FieldWrapper
            data-testid="create-credential-form-sshKey"
            maxWidth="40rem"
            label={tScan('credentialedScanningForm.sshKey')}
            error={errors.sshKey}
            isRequired
            field={
              <Controller
                control={control}
                name="sshKey"
                render={({ field: { value, onChange, ...rest } }) => (
                  <TextareaField
                    rows={4}
                    style={{
                      resize: 'vertical',
                    }}
                    {...rest}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            }
          />
        )}
        <HStack width="full" xAlign="right">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('ui:footer.closeButtonText')}
          </Button>
          <Button
            type="submit"
            isDisabled={formHasError}
            isLoading={
              isAddCredentialedScanMutationPending ||
              isUpdateCredentialedScanMutationPending
            }
          >
            {tScan(
              `credentialedScanningForm.actionCredentials${
                scanCredentialFormData ? 'Edit' : 'Create'
              }`,
            )}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
